import { apiClient } from '@api/client';
import { nonNull } from '@helpers/non-null';
import type { InstalledIntegrationDetailsProps } from '@pages/AdminSettings/components/IntegrationTab/constants';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Divider, Form, Switch, Typography } from 'antd';
import React, { useState } from 'react';

const { Title, Paragraph } = Typography;

export default function AsanaInstalledDetails({
  integration
}: InstalledIntegrationDetailsProps) {
  const { data, refetch } = useQuery({
    suspense: true,
    queryKey: ['asana', 'options', integration.id],
    queryFn: async () => {
      const { data } = await apiClient.asana.asanaControllerRegistrationInfo({
        integrationId: integration.id
      });
      return data;
    }
  });

  const { createReviewerSubtasks } = nonNull(data);

  const [optionsForm] = Form.useForm<{ createReviewerSubtasks: boolean }>();
  const [isTouched, setIsTouched] = useState(false);

  const updateOptions = useMutation({
    mutationFn: async (values: { createReviewerSubtasks: boolean }) => {
      await apiClient.asana.asanaControllerUpdateOptions({
        integrationId: integration.id,
        createReviewerSubtasks: values.createReviewerSubtasks
      });
    },
    onSettled: async () => {
      await refetch();
      setIsTouched(false);
      setTimeout(() => {
        optionsForm.resetFields();
      }, 0);
    }
  });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Title level={3}>Asana Integration Settings</Title>
      <Paragraph style={{ maxWidth: 600 }}>
        Configure how StreamWork interacts with your Asana workspace.
      </Paragraph>
      <Divider />
      <Form
        form={optionsForm}
        layout="vertical"
        initialValues={{ createReviewerSubtasks }}
        style={{ marginBottom: 16 }}
        onFinish={(values) => updateOptions.mutateAsync(values)}
        onFieldsChange={() => setIsTouched(true)}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16
          }}
        >
          <Form.Item
            name="createReviewerSubtasks"
            valuePropName="checked"
            noStyle
          >
            <Switch />
          </Form.Item>
          <div>Create a subtask for each reviewer in Asana</div>
        </div>
        <Paragraph
          type="secondary"
          style={{ marginBottom: 0, maxWidth: '60ch' }}
        >
          When an approval workflow is attached to an Asana task, StreamWork
          will automatically create a subtask for each reviewer, making it easy
          to track individual review progress directly in Asana.
        </Paragraph>
      </Form>
      <div
        style={{
          display: 'flex',
          marginTop: 'auto',
          justifyContent: 'flex-end'
        }}
      >
        <Button
          type="primary"
          loading={updateOptions.isLoading}
          onClick={() => optionsForm.submit()}
          disabled={!isTouched}
        >
          Update Settings
        </Button>
      </div>
    </div>
  );
}
